import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';

import { LOGIN, REGISTER } from '../../graphql/mutations';
import { trackEvent } from '../../utils/gt';
import { signupAcquisition } from '../../utils/acquisition';
import { trackMetaSignup } from '../../utils/metaPixel';
import { authErrorKind, authErrorMessage } from '../../utils/authErrors';

const API_URL = import.meta.env.VITE_GRAPHQL_URL.replace('/graphql', '');
const COOLDOWN_SECONDS = 60;

/**
 * The requests behind signing in, for the sign-in window (AuthDialog) and the installed app's screens (AppWelcome):
 * login, signup, the password link and the confirmation email, with the Turnstile check, the busy state, the
 * server's answer and the cooldowns. Each screen says where the person goes next:
 * - onLoggedIn({ fromSignupLink }), onSignedUp(): signed in, the token is saved;
 * - onSignupSent(): the account waits on the email (only if the API asks for it again);
 * - onResetSent(): the password link went out.
 */
export const useAuthRequests = ({ afterSignupPath, onLoggedIn, onSignedUp, onSignupSent, onResetSent }) => {
  const [busy, setBusy] = useState(null); // null | 'verify' (waiting on Turnstile) | 'send'
  const [error, setError] = useState(null); // { kind, action }
  const [badField, setBadField] = useState(null); // the field the server's answer marks red
  const [sentTo, setSentTo] = useState('');
  const [resetCooldown, setResetCooldown] = useState(0);
  const [confirmCooldown, setConfirmCooldown] = useState(0);
  const emailRef = useRef(''); // the email typed so far, carried from one form to the next
  const turnstileRef = useRef(null);

  const [loginMutation] = useMutation(LOGIN);
  const [signUpMutation] = useMutation(REGISTER);

  useEffect(() => {
    if (resetCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResetCooldown(resetCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resetCooldown]);

  useEffect(() => {
    if (confirmCooldown <= 0) return undefined;
    const timer = setTimeout(() => setConfirmCooldown(confirmCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [confirmCooldown]);

  const clearErrors = useCallback(() => {
    setError(null);
    setBadField(null);
  }, []);

  const start = useCallback(() => {
    setBusy(null);
    clearErrors();
    emailRef.current = '';
  }, [clearErrors]);

  const fail = (kind, action) => {
    setError({ kind, action });
    setBusy(null);
  };

  // Each Turnstile token is good for one try: wait for this one, take a fresh one whatever the answer
  const turnstileToken = async () => {
    setBusy('verify');
    const token = await turnstileRef.current?.getToken();
    setBusy('send');
    return token || '';
  };

  const handleLogin = async ({ identifier, password }) => {
    if (busy) return;
    trackEvent('modal_login_submit', 'Modal Login Submit', 'modal_login', true);
    clearErrors();
    emailRef.current = identifier;
    const token = await turnstileToken();
    try {
      const { data } = await loginMutation({
        variables: { input: { identifier, password, provider: 'local' } },
        context: { headers: { 'X-Turnstile-Token': token } },
      });
      const jwt = data?.login?.jwt;
      if (!jwt) throw new Error('The login answered without a token');
      localStorage.setItem('authToken', jwt);
      // An account made through the signup ads' link (/?cadastro=1) starts on creating a workout, once
      const fromSignupLink = localStorage.getItem('mt-signup-link') === '1';
      localStorage.removeItem('mt-signup-link');
      setBusy(null);
      onLoggedIn({ fromSignupLink });
    } catch (requestError) {
      const kind = authErrorKind(requestError, 'login');
      if (kind === 'unconfirmed') trackEvent('modal_login_not_confirmed', 'Modal Login Not Confirmed', 'modal_login', true);
      if (kind === 'wrong') {
        trackEvent('modal_login_wrong_pwd', 'Modal Login Wrong Password', 'modal_login', true);
        setBadField('password');
      }
      fail(kind, 'login');
    } finally {
      turnstileRef.current?.reset();
    }
  };

  const handleSignup = async (values) => {
    if (busy) return;
    trackEvent('modal_signup_submit', 'Modal Signup Submit', 'modal_signup', true);
    clearErrors();
    const email = values.email.trim();
    emailRef.current = email;
    const token = await turnstileToken();
    const acquisition = signupAcquisition();
    try {
      const { data } = await signUpMutation({
        variables: {
          input: {
            name: values.name.trim(),
            // No username field: Strapi needs a unique one, and the email already is
            username: email.toLowerCase(),
            email,
            password: values.password,
            origin: window.origin,
            acquisition,
          },
        },
        context: { headers: { 'X-Turnstile-Token': token } },
      });
      // The account exists either way (with or without email confirmation): that's the signup Meta counts, with the
      // event id the API sends through the Conversions API
      trackMetaSignup(acquisition.eventId);
      const jwt = data?.register?.jwt;
      setBusy(null);
      if (jwt) {
        localStorage.setItem('authToken', jwt);
        onSignedUp();
      } else {
        // Email confirmation first: the first login after it takes the ads' signup to /workout
        if (afterSignupPath) localStorage.setItem('mt-signup-link', '1');
        setSentTo(email);
        onSignupSent();
      }
    } catch (requestError) {
      const kind = authErrorKind(requestError, 'signup');
      if (kind === 'taken') {
        trackEvent('modal_signup_user_taken', 'Modal Signup User Taken', 'modal_signup', true);
        setBadField('email');
      } else if (kind !== 'turnstile') {
        trackEvent('modal_signup_general_error', `Modal Signup General Error - ${authErrorMessage(requestError)}`, 'modal_signup', true);
      }
      fail(kind, 'signup');
    } finally {
      turnstileRef.current?.reset();
    }
  };

  // The API answers the same whether or not the email has an account (an API older than meu-treino-api d9100cc said
  // 400 "Email not found": that's the same answer here too)
  const requestReset = async (email) => {
    if (busy) return;
    trackEvent('forgot_password_submit', 'Forgot Password - Submit', 'forgot_password', true);
    setError(null);
    setBusy('send');
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password-custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, url: `${window.location.origin}/reset-password` }),
      });
      if (!response.ok && response.status !== 400) return fail('server', 'forgot');
      setSentTo(email);
      setResetCooldown(COOLDOWN_SECONDS);
      setBusy(null);
      onResetSent();
    } catch {
      fail('network', 'forgot');
    }
  };

  const resendConfirmation = async (action) => {
    const email = (sentTo || emailRef.current).trim();
    if (!email || confirmCooldown > 0) return;
    try {
      await fetch(`${API_URL}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setConfirmCooldown(COOLDOWN_SECONDS);
    } catch {
      setError({ kind: 'network', action });
    }
  };

  return {
    busy,
    error,
    badField,
    setBadField,
    sentTo,
    resetCooldown,
    confirmCooldown,
    emailRef,
    turnstileRef,
    start,
    clearErrors,
    handleLogin,
    handleSignup,
    requestReset,
    resendConfirmation,
  };
};
